import React, { useEffect, useState } from "react";
import {
  Card,
  Space,
  Row,
  Col,
  notification,
  Table,
  TableColumnsType,
  Typography,
  Button,
  message,
  Spin,
  Tooltip,
  Select,
  Tag,
  Progress,
  Modal,
  Dropdown,
} from "antd";
import { useParams, Link, useNavigate } from "react-router-dom";
import { TestSetService } from "../../services/TestSetService";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import { ChatCompletionRequest } from "../../model_gen/chat";
import {
  CopyOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  UpOutlined,
  DownOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { TestSet, TestRun, IntentGroup } from "../../model_gen/evaluation";
import { NEW_TEST_RUN_STORAGE_KEY } from "../TestRun/TestRunNew";
import RequestView from "../shared/RequestView";
import { TestRunService } from "../../services/TestRunService";
import { IntentService } from "../../services/IntentService";
import { TestSetSemanticChart } from "./TestSetSemanticChart";
import TestRunComparison from "../TestRun/TestRunComparison";
const { Text, Title } = Typography;

const TestSetDetail: React.FC = () => {
  const { intentGroupId, testSetId } = useParams();
  const { apiKey } = usePermissions();
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [requests, setRequests] = useState<ChatCompletionRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(5);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, ChatCompletionRequest[]>
  >({});
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [isTestRunsLoading, setIsTestRunsLoading] = useState<boolean>(false);
  const [currentTestRunPage, setCurrentTestRunPage] = useState<number>(1);
  const [testRunsByPage, setTestRunsByPage] = useState<
    Record<number, TestRun[]>
  >({});
  const [totalTestRuns, setTotalTestRuns] = useState<number>(0);
  const [tagDistribution, setTagDistribution] = useState<any[]>([]);
  const [testRunProgress, setTestRunProgress] = useState<Record<string, any>>(
    {}
  );
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isCompareMode, setIsCompareMode] = useState<boolean>(false);
  const [selectedTestRunIds, setSelectedTestRunIds] = useState<number[]>([]);
  const [isComparisonModalVisible, setIsComparisonModalVisible] =
    useState<boolean>(false);

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        false,
        setIsLoading,
        apiKey
      ).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (testSetId) {
      setIsLoading(true);
      // Fetch the TestSet
      TestSetService.getTestSet(testSetId, setIsLoading, apiKey).subscribe(
        (data: TestSet) => {
          setTestSet(data);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test set: ${error}`,
          });
          setIsLoading(false);
        }
      );
      if (!requestsByPage[currentPage]) {
        fetchRequests(currentPage);
      } else {
        setRequests(requestsByPage[currentPage]);
      }
    }
  }, [testSetId, currentPage, requestPageSize]);

  useEffect(() => {
    if (testSetId && !testRunsByPage[currentTestRunPage]) {
      fetchTestRuns(currentTestRunPage);
    } else if (testRunsByPage[currentTestRunPage]) {
      setTestRuns(testRunsByPage[currentTestRunPage]);
    }
  }, [testSetId, currentTestRunPage]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSetRequestDistribution(
        testSetId,
        null,
        apiKey
      ).subscribe(
        (response: any[]) => {
          setTagDistribution(response);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch tag distribution: ${error}`,
          });
        }
      );
    }
  }, [testSetId]);

  useEffect(() => {
    const fetchProgress = () => {
      if (!testSetId) return;

      TestRunService.getTestRunProgressByTestSet(
        Number(testSetId),
        null,
        apiKey
      ).subscribe(
        (progressData: Record<string, any>) => {
          setTestRunProgress(progressData);
        },
        (error: any) => {
          console.error(error);
        }
      );
    };

    // Check if any test runs need monitoring
    const hasIncompleteRuns = testRuns.some(
      (run) => run.status !== "COMPLETED" && run.status !== "ERROR"
    );

    // Only set up interval if there are incomplete runs
    if (hasIncompleteRuns) {
      // Run immediately
      fetchProgress();

      // Then set up interval
      const intervalId = setInterval(fetchProgress, 5000);

      // Cleanup on unmount
      return () => clearInterval(intervalId);
    }
  }, [testSetId, apiKey, testRuns]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * requestPageSize;
    setIsRequestsLoading(true);
    TestSetService.getTestSetRequests(
      testSetId,
      -1,
      -1,
      offset,
      requestPageSize,
      null,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setRequests(response.items);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsRequestsLoading(false);
      }
    );
  };

  const fetchTestRuns = (page: number) => {
    const offset = (page - 1) * 5;
    setIsTestRunsLoading(true);
    TestRunService.getTestRunsByTestSet(
      Number(testSetId),
      offset,
      5,
      null,
      apiKey
    ).subscribe(
      (response: { items: TestRun[]; total: number }) => {
        setTestRuns(response.items);
        setTotalTestRuns(response.total);
        setTestRunsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsTestRunsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch test runs: ${error}`,
        });
        setIsTestRunsLoading(false);
      }
    );
  };

  const handleTagsChange = (requestId: string, newTags: string[]) => {
    setIsRequestsLoading(true);
    const request = requests.find((r) => r.request_id === requestId);
    if (!request) return;

    try {
      const metadata = JSON.parse(
        (request.metadata?._maitai_test_request_meta as string) || "{}"
      );
      metadata.tags = newTags;

      TestSetService.updateTestSetRequestTags(
        testSetId,
        requestId,
        newTags,
        null,
        apiKey
      ).subscribe(
        () => {
          // Update the local state
          setRequests((prev) =>
            prev.map((r) => {
              if (r.request_id === requestId) {
                return {
                  ...r,
                  metadata: {
                    ...r.metadata,
                    _maitai_test_request_meta: JSON.stringify(metadata),
                  },
                };
              }
              return r;
            })
          );
          setIsRequestsLoading(false);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to update tags",
          });
          setIsRequestsLoading(false);
        }
      );
    } catch (e) {
      console.error(e);
      setIsRequestsLoading(false);
    }
  };

  const updateTestSetRequest = (request: ChatCompletionRequest) => {
    setRequests((prev) =>
      prev.map((r) => (r.request_id === request.request_id ? request : r))
    );
  };

  const handleNewTestRun = () => {
    // Clear any existing test run data
    localStorage.removeItem(NEW_TEST_RUN_STORAGE_KEY);
    // Navigate to new test run page
    navigate(
      `/intent-group/${testSet.intent_group_id}/testsets/${testSetId}/runs/new`
    );
  };

  const handleDeleteTestRun = (testRun: TestRun) => {
    Modal.confirm({
      title: "Delete Test Run",
      content: "Are you sure you want to delete this test run?",
      okText: "Yes",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: () => {
        TestRunService.deleteTestRun(testRun.id, null, apiKey).subscribe(
          () => {
            message.success("Test run deleted successfully");
            // Clear the test runs page cache
            setTestRunsByPage({});
            // Reset to first page
            setCurrentTestRunPage(1);
            // Fetch fresh data
            fetchTestRuns(1);
          },
          (error: any) => {
            console.error(error);
            notification.error({
              message: "Error",
              description: `Failed to delete test run: ${error}`,
            });
          }
        );
      },
    });
  };

  const handleCompareClick = () => {
    setIsCompareMode(true);
    setSelectedTestRunIds([]);
  };

  const handleCancelCompare = () => {
    setIsCompareMode(false);
    setSelectedTestRunIds([]);
  };

  const handleCompareRuns = () => {
    setIsComparisonModalVisible(true);
  };

  const handleCloseComparisonModal = () => {
    setIsComparisonModalVisible(false);
    // Reset comparison mode and selected test runs when modal closes
    setIsCompareMode(false);
    setSelectedTestRunIds([]);
  };

  const requestColumns: TableColumnsType<ChatCompletionRequest> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Tags",
      key: "tags",
      width: "30%",
      render: (_, request: ChatCompletionRequest) => {
        try {
          const metaData = JSON.parse(
            (request.metadata?._maitai_test_request_meta as string) || "{}"
          );
          const tags = metaData.tags || [];
          return (
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Add tags"
              defaultValue={tags}
              tokenSeparators={[","]}
              options={tagDistribution.map(({ tag }) => ({
                label: tag,
                value: tag,
              }))}
              onClick={(e) => e.stopPropagation()}
              maxTagTextLength={10}
              maxTagCount={5}
              onBlur={(e) => {
                const selectContainer = e.target.closest(".ant-select");
                if (!selectContainer) return;

                const selectedItems = selectContainer.querySelectorAll(
                  ".ant-select-selection-item"
                );
                const newTags = Array.from(selectedItems).map(
                  (item: any) => item.textContent || ""
                );

                if (JSON.stringify(newTags) !== JSON.stringify(tags)) {
                  handleTagsChange(request.request_id, newTags);
                }
              }}
            />
          );
        } catch (e) {
          return null;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "35%",
      align: "right",
      render: (_, request: ChatCompletionRequest) => {
        const requestId = request.request_id || "";
        return (
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            {requestId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_, record: ChatCompletionRequest) => (
        <Link
          to={`/application/${
            record.application_id
          }/session/${encodeURIComponent(record.session_id)}/request/${
            record.request_id
          }`}
          target="_blank">
          <Tooltip
            title={
              <RequestView
                request={record}
                apiKey={apiKey}
                testSetId={testSetId}
                testSetRequestId={
                  record.metadata?._maitai_test_request_id as string
                }
                isEditable={true}
                onUpdated={(request: ChatCompletionRequest) => {
                  updateTestSetRequest(request);
                }}
              />
            }
            placement="left"
            overlayStyle={{ maxWidth: "600px", width: "600px" }}>
            <Button type="link" icon={<EyeOutlined />}>
              View
            </Button>
          </Tooltip>
        </Link>
      ),
    },
  ];

  const testRunColumns: TableColumnsType<TestRun> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Date Completed",
      dataIndex: "date_completed",
      key: "date_completed",
      width: "20%",
      render: (text: number) =>
        text ? moment(text).format("YYYY-MM-DD hh:mm:ss A") : "-",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "20%",
      render: (text: string) => text || "-",
    },
    {
      title: "Pass Rate",
      key: "pass_rate",
      width: "15%",
      render: (_, record: TestRun) => {
        const summary = record.qualitative_evaluation_summary;
        if (!summary?.request_scores?.length || !summary.completed_requests) {
          return "-";
        }

        // Calculate total passing requests (score >= 3)
        const passingRequests = summary.request_scores
          .filter((score: any) => score.score >= 3)
          .reduce((acc: any, score: any) => acc + score.count, 0);

        // Only use completed requests in denominator
        const passRate = (passingRequests / summary.completed_requests) * 100;

        return (
          <Tooltip
            title={`${passingRequests} out of ${summary.completed_requests} completed requests scored 3 or higher`}>
            <Tag
              color={
                passRate >= 80
                  ? "success"
                  : passRate >= 60
                  ? "warning"
                  : "error"
              }>
              {passRate.toFixed(1)}%
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Avg Time",
      key: "avg_time",
      width: "10%",
      render: (_, record: TestRun) => {
        const summary = record.qualitative_evaluation_summary;
        if (!summary?.response_time_percentiles?.p50) {
          return "-";
        }
        return (
          <Tooltip
            title={
              <div>
                <div>
                  P50: {Math.round(summary.response_time_percentiles.p50)}ms
                </div>
                <div>
                  P90: {Math.round(summary.response_time_percentiles.p90)}ms
                </div>
                <div>
                  P95: {Math.round(summary.response_time_percentiles.p95)}ms
                </div>
              </div>
            }>
            <Text>{Math.round(summary.response_time_percentiles.p50)}ms</Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: "15%",
      render: (_, record: TestRun) => {
        const progressData = testRunProgress[record.id.toString()];
        const status = progressData?.status || record.status;

        const statusConfig: Record<string, { color: string }> = {
          CREATED: { color: "default" },
          COMPLETED: { color: "success" },
          ERROR: { color: "error" },
          PENDING: { color: "processing" },
        };

        if (status !== "RUNNING") {
          return (
            <Tag color={statusConfig[status]?.color || "default"}>{status}</Tag>
          );
        }

        if (!progressData) {
          return <Spin size="small" />;
        }

        const {
          completed_requests,
          error_requests,
          completed_evals,
          total_requests,
        } = progressData;

        let percent = 0;
        if (!total_requests) {
          percent = 0;
        } else if (completed_evals > 0) {
          // When evaluation data exists, use completed_requests as denominator
          percent =
            completed_requests > 0
              ? 50 + Math.min(50, (completed_evals / completed_requests) * 50)
              : 0;
        } else {
          percent = (completed_requests / total_requests) * 50;
        }
        // Clamp progress for uncompleted test runs below 100%
        percent = Math.min(percent, 99.9);

        const tooltipText =
          completed_evals > 0
            ? `${completed_evals}/${completed_requests} evaluations completed`
            : `${completed_requests}/${total_requests} requests completed with ${error_requests} errors`;

        return (
          <Tooltip title={tooltipText}>
            <Progress
              percent={Number(percent.toFixed(1))}
              size="small"
              status="active"
            />
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "48px",
      render: (_, record: TestRun) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleDeleteTestRun(record);
                },
              },
            ],
          }}
          trigger={["click"]}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}>
          <Col flex="auto">
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              {!intentGroup || !testSet ? (
                <Spin size="small" />
              ) : (
                <>
                  <Link to={"/intent-groups"} style={{ color: "#828282" }}>
                    Intent Groups
                    {" / "}{" "}
                  </Link>
                  <Link
                    to={`/intent-group/${testSet.intent_group_id}`}
                    style={{ color: "#828282" }}>
                    {intentGroup.intent_name}
                    {" / "}{" "}
                  </Link>
                  <Link
                    to={`/intent-group/${testSet.intent_group_id}/testsets`}
                    style={{ color: "#828282" }}>
                    Test Sets
                    {" / "}{" "}
                  </Link>
                  {testSet.name}
                </>
              )}
            </Typography>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() =>
                navigate(
                  `/intent-group/${testSet.intent_group_id}/testsets/${testSetId}/edit`
                )
              }>
              Edit
            </Button>
          </Col>
        </Row>

        {testSet && (
          <>
            <Card
              size="small"
              title={
                <Text strong style={{ fontSize: "16px" }}>
                  Details
                </Text>
              }
              style={{
                background: "#f8f9fa",
                borderRadius: "8px",
                marginBottom: "12px",
              }}
              loading={isLoading}>
              <Row gutter={[16, 16]}>
                <Col span={10}>
                  <Text type="secondary">Description</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {testSet.description || "No description provided"}
                    </Text>
                  </div>
                </Col>
                <Col span={6}>
                  <Text type="secondary">Date Created</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {moment(testSet.date_created).format(
                        "MMM D, YYYY HH:mm:ss"
                      )}
                    </Text>
                  </div>
                </Col>
                <Col span={4}>
                  <Text type="secondary">Total Requests</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {(testSet.meta?.request_count as number) || 0}
                    </Text>
                  </div>
                </Col>
                <Col span={4}>
                  <Text type="secondary">Golden</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {(testSet as any).is_golden ? "Yes" : "No"}
                    </Text>
                  </div>
                </Col>
                {tagDistribution.length > 0 && (
                  <Col span={24}>
                    <Text type="secondary">Request Distribution</Text>
                    <div style={{ marginTop: "8px" }}>
                      <Space wrap>
                        {tagDistribution.map(({ tag, count }) => (
                          <Tag
                            key={tag}
                            bordered={true}
                            style={{
                              borderRadius: "16px",
                              padding: "4px 12px",
                              fontSize: "14px",
                            }}>
                            {tag} ({count})
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  </Col>
                )}
              </Row>
            </Card>

            <Card
              size="small"
              title={
                <Space
                  style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    Semantic Distribution
                  </Text>
                  <div
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ cursor: "pointer" }}>
                    {isExpanded ? <UpOutlined /> : <DownOutlined />}
                  </div>
                </Space>
              }
              style={{
                background: "#f8f9fa",
                borderRadius: "8px",
                marginBottom: "12px",
              }}>
              <TestSetSemanticChart
                testSetId={testSetId}
                apiKey={apiKey}
                isExpanded={isExpanded}
              />
            </Card>
          </>
        )}

        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ marginBottom: "16px", marginTop: 0 }}>
              Test Runs
            </Title>
          </Col>
          <Col>
            <Space>
              {!isCompareMode ? (
                <>
                  <Button icon={<SwapOutlined />} onClick={handleCompareClick}>
                    Compare Runs
                  </Button>
                  <Button type="primary" onClick={handleNewTestRun}>
                    New Test Run
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={handleCancelCompare}>Cancel</Button>
                  <Button
                    type="primary"
                    onClick={handleCompareRuns}
                    disabled={selectedTestRunIds.length < 2}>
                    Compare
                  </Button>
                </>
              )}
            </Space>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={testRunColumns}
          dataSource={testRuns}
          loading={isTestRunsLoading}
          rowSelection={
            isCompareMode
              ? {
                  type: "checkbox",
                  onChange: (selectedRowKeys) => {
                    setSelectedTestRunIds(selectedRowKeys as number[]);
                  },
                  getCheckboxProps: (record) => {
                    const recordStatus: string =
                      testRunProgress[record.id.toString()]?.status ||
                      record.status;
                    return {
                      disabled:
                        recordStatus === "CREATED" ||
                        recordStatus === "PENDING" ||
                        recordStatus === "RUNNING",
                    };
                  },
                }
              : undefined
          }
          pagination={{
            current: currentTestRunPage,
            pageSize: 5,
            total: totalTestRuns,
            onChange: (page) => setCurrentTestRunPage(page),
          }}
          onRow={(record) => {
            const recordStatus: string =
              testRunProgress[record.id.toString()]?.status || record.status;
            return {
              onClick: () => {
                if (
                  !isCompareMode &&
                  recordStatus !== "CREATED" &&
                  recordStatus !== "PENDING" &&
                  recordStatus !== "RUNNING"
                ) {
                  navigate(
                    `/intent-group/${testSet.intent_group_id}/testsets/${testSetId}/runs/${record.id}`
                  );
                }
              },
              style: {
                cursor: isCompareMode
                  ? "default"
                  : recordStatus !== "CREATED" &&
                    recordStatus !== "PENDING" &&
                    recordStatus !== "RUNNING"
                  ? "pointer"
                  : "not-allowed",
                opacity:
                  recordStatus === "CREATED" ||
                  recordStatus === "PENDING" ||
                  recordStatus === "RUNNING"
                    ? 0.75
                    : 1,
                backgroundColor:
                  recordStatus === "CREATED" ||
                  recordStatus === "PENDING" ||
                  recordStatus === "RUNNING"
                    ? "#f5f5f5"
                    : "inherit",
              },
            };
          }}
        />

        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ marginBottom: "16px" }}>
              Requests
            </Title>
          </Col>
        </Row>

        <Table
          dataSource={requests}
          columns={requestColumns}
          rowKey="request_id"
          loading={isRequestsLoading}
          pagination={{
            current: currentPage,
            pageSize: requestPageSize,
            total: Number(testSet?.meta?.request_count) || 0,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              if (pageSize && pageSize !== requestPageSize) {
                setRequestPageSize(pageSize);
                setRequestsByPage({});
              }
            },
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onShowSizeChange: (current, size) => {
              setCurrentPage(1);
              setRequestPageSize(size);
              setRequestsByPage({});
            },
          }}
        />

        {/* Comparison Modal */}
        <Modal
          title="Test Run Comparison"
          open={isComparisonModalVisible}
          onCancel={handleCloseComparisonModal}
          width={1000}
          footer={[
            <Button key="close" onClick={handleCloseComparisonModal}>
              Close
            </Button>,
          ]}
          destroyOnClose={true}>
          <TestRunComparison
            testRunIds={selectedTestRunIds}
            apiKey={apiKey}
            key={selectedTestRunIds.join("-")}
          />
        </Modal>
      </Space>
    </Card>
  );
};

export default TestSetDetail;
